// Candidate-scope resolution for speaker matching (round-28, 2026-08-31). User feedback: during a
// LIVE MATCH, "punto mío"/"punto tuyo" was cosine-matched against nothing narrower than "every
// enrolled profile" in some code paths — fine when there are a handful of players enrolled, a
// real correctness risk once the roster grows (a bystander's voice could outscore the actual
// match players). The fix is a small, explicit, PURE notion of "who could plausibly be speaking
// right now" that every speaker-matching call site is handed, rather than a hidden global — see
// DESIGN.md's candidate-scope section for the full ladder and rationale.
//
// Explicit-parameter design (not a store the matching call reads): voiceScore.ts's
// rankSpeakerCandidates/bestSpeakerMatch/classifyConfidenceBand already take a `candidates` array
// — scoping is just "filter that array before calling them," which every existing call site can
// do without restructuring how those pure functions work. This module is the ONE place that
// filter logic lives, so the four call sites (cornerClaim.ts, LiveMatch.vue, confirmFlow.ts's
// observability row, DebugStt.vue's transcript display) can't drift on what "scope" means.
//
// The ladder (narrowest first — the caller picks the tightest one that actually applies):
//  1. 'match'      — a LIVE match is in progress: exactly its two current players. Never widened
//                     even if neither has a voice profile (see resolvePointClaim in
//                     LiveMatch.vue for the graceful "can't tell them apart" handling).
//  2. 'tournament' — resolving who's speaking within a tournament's own flow (roll-call,
//                     check-in, next-up) before any specific match exists yet: that tournament's
//                     registered participants only.
//  3. 'picked'     — plain match setup (NewMatch, no tournament slot): whichever of the two
//                     draft sides are already filled. Once a corner claim already occupies EITHER
//                     side, the OTHER claim is much more likely to be the two people actually at
//                     the table than a random third enrolled player — same narrowing logic as
//                     'match', one step earlier. Falls back to 'all' the moment nobody is picked
//                     yet (the very first claim has nothing to narrow against).
//  4. 'all'        — every enrolled voice profile. The original, unscoped behavior — still
//                     correct for Home's "who just walked up" claim and any other context with no
//                     narrower candidate pool to reason about.
import type { SpeakerCandidate } from '../voiceScore'

export type CandidateScope =
  | { kind: 'match'; playerIds: number[] }
  | { kind: 'tournament'; tournamentId: number; playerIds: number[] }
  | { kind: 'picked'; playerIds: number[] }
  | { kind: 'all' }

export function matchScope(playerIds: number[]): CandidateScope {
  return { kind: 'match', playerIds }
}

export function tournamentScope(tournamentId: number, playerIds: number[]): CandidateScope {
  return { kind: 'tournament', tournamentId, playerIds }
}

/** NewMatch's draft — pass whichever of draft.left?.id / draft.right?.id are currently filled.
 *  Empty input naturally falls back to 'all' (nothing picked yet to narrow against). */
export function pickedScope(playerIds: number[]): CandidateScope {
  return playerIds.length > 0 ? { kind: 'picked', playerIds } : { kind: 'all' }
}

export function allScope(): CandidateScope {
  return { kind: 'all' }
}

/** Filters a full candidate list down to the scope's player ids — 'all' is a no-op (identity),
 *  every other kind keeps only candidates whose id is in the scope's playerIds. Order preserved. */
export function filterCandidatesByScope<T extends { id: number }>(candidates: T[], scope: CandidateScope): T[] {
  if (scope.kind === 'all') return candidates
  const ids = new Set(scope.playerIds)
  return candidates.filter((c) => ids.has(c.id))
}

/** Human-readable label for DebugStt / toasts — "scope: match players (2)" etc. `resolvedCount`
 *  is the number of candidates the scope actually resolved to (after filtering against whoever
 *  has a voice profile at all) so a scope that's nominally "2 players" but only 1 is enrolled
 *  shows that honestly instead of overclaiming. */
export function scopeLabel(scope: CandidateScope, resolvedCount: number): string {
  switch (scope.kind) {
    case 'match':
      return `match players (${resolvedCount})`
    case 'tournament':
      return `tournament participants (${resolvedCount})`
    case 'picked':
      return `picked players (${resolvedCount})`
    case 'all':
      return `all enrolled (${resolvedCount})`
  }
}

/** Compact shape stitched onto stt.ts's transcript log / DebugStt display — never the full
 *  candidate list, just what's needed to render "scope: X (n)". */
export interface ScopeSummary {
  kind: CandidateScope['kind']
  label: string
}

export function summarizeScope(scope: CandidateScope, resolvedCount: number): ScopeSummary {
  return { kind: scope.kind, label: scopeLabel(scope, resolvedCount) }
}

/** Convenience: apply a scope to a raw voice-profile list and produce the SpeakerCandidate[]
 *  voiceScore.ts's ranking functions expect, in one call. */
export function scopedCandidates(
  profiles: { playerId: number; centroid?: number[] | null; spkModelVersion?: string | null }[],
  scope: CandidateScope,
): SpeakerCandidate[] {
  const all: SpeakerCandidate[] = profiles
    .filter((p) => p.centroid && p.centroid.length > 0)
    .map((p) => ({ id: p.playerId, centroid: p.centroid!, spkModelVersion: p.spkModelVersion }))
  return filterCandidatesByScope(all, scope)
}
